import axios from 'axios';

/**
 * consent.js — captura de consentimiento de comunicaciones (WhatsApp).
 *
 * El consentimiento se guarda en el investments-api (api-beta), que valida el
 * mismo JWT del cliente y comparte MongoDB con la compuerta de Senales. El
 * documento queda en la coleccion `consents` (append-only, auditable): la
 * compuerta de Senales lo lee para decidir si un cliente puede recibir mensajes.
 *
 * DISENO: TODO es best-effort. Si el endpoint aun no esta desplegado, estas
 * funciones NO lanzan ni bloquean — devuelven null y el modal cae al flag local.
 * Asi el frontend nunca se rompe por el backend.
 */
const API = axios.create({
  baseURL: import.meta.env.VITE_INVESTMENTS_API_URL || 'http://localhost:5001',
  headers: { 'Content-Type': 'application/json' },
  timeout: 6000,
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// El backend fija la version de politica (POLICY_VERSION 'v1-2026-06' en
// services/consent.js). Aqui guardamos una REFERENCIA del texto exacto que vio
// el cliente (evidencia legal, campo textoMostrado). Subir cuando cambie el copy.
export const CONSENT_TEXTO_REF =
  'modal_whatsapp_v1: "¿Quieres que Alex te acompane por WhatsApp?" — extracto mensual, avance de metas, oportunidades, respuestas de Alex. Opcional y revocable.';

// Flag local: evita re-mostrar el modal en el mismo navegador aunque el backend
// aun no responda. La fuente de verdad sigue siendo el backend.
const LOCAL_FLAG = 'fp_wa_consent_v1';

export function markConsentDecidedLocal(otorgado) {
  try {
    localStorage.setItem(LOCAL_FLAG, JSON.stringify({ otorgado: !!otorgado, at: new Date().toISOString() }));
  } catch { /* noop */ }
}

export function getLocalConsentFlag() {
  try {
    const raw = localStorage.getItem(LOCAL_FLAG);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

/**
 * Consulta si el usuario autenticado ya decidio el consentimiento de WhatsApp.
 * Backend real: GET /api/consent/me -> { canal, otorgado, versionPolitica, fecha, yaRespondio }.
 * Devuelve { decided: bool, whatsapp: bool } o null si el endpoint no responde.
 */
export async function getConsentStatus() {
  try {
    const { data } = await API.get('/api/consent/me');
    if (data && typeof data.yaRespondio === 'boolean') {
      return { decided: data.yaRespondio, whatsapp: !!data.otorgado };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Registra la decision de consentimiento (canal WhatsApp).
 * @param {boolean} otorgado  true = autoriza, false = ahora no.
 * @param {object}  opts       { telefono, fuente }
 * Devuelve true si el backend confirmo, false si no (pero nunca lanza).
 */
export async function saveConsent(otorgado, opts = {}) {
  // Backend real: POST /api/consent body { otorgado, fuente, telefono, textoMostrado }.
  // El server agrega userId (del JWT), ip, userAgent, canal y versionPolitica.
  const payload = {
    otorgado: !!otorgado,
    telefono: opts.telefono || null,
    fuente: opts.fuente || 'modal_inapp',
    textoMostrado: CONSENT_TEXTO_REF,
  };
  markConsentDecidedLocal(otorgado); // marca local siempre (no volver a molestar)
  try {
    const { data } = await API.post('/api/consent', payload);
    return !!(data && data.ok);
  } catch {
    return false; // si el backend no responde, el flag local ya evita re-mostrar
  }
}
